/**
 * Rhythmz — ComputePipeline
 * ─────────────────────────
 * GPU Compute Shader Wrapper with VRAM tracking.
 *
 * Manages the lifecycle of GPU compute effects (particles, fluids, custom) and
 * tracks VRAM allocation against a user-configurable budget (default 16GB).
 *
 * Console-logging every allocation, scale-up, and budget warning with
 * [GPU VRAM Allocator] prefix for verification.
 */

import * as THREE from 'three/webgpu';

export interface ComputeEffect {
  id: string;
  name: string;
  vramMB: number;
  workgroups: number;
  enabled: boolean;
  dispatch: () => void;
  dispose: () => void;
  scale: number;
}

export interface VRAMSnapshot {
  totalAllocatedMB: number;
  totalBudgetMB: number;
  effects: { id: string; name: string; vramMB: number; enabled: boolean; scale: number }[];
  utilizationPercent: number;
}

export class ComputePipeline {
  private effects: Map<string, ComputeEffect> = new Map();
  private budget: number;
  private totalVRAM = 0;
  private _renderer: THREE.WebGPURenderer | null = null;

  /** Whether a renderer is attached. */
  get hasRenderer(): boolean { return this._renderer !== null; }

  onBudgetWarning: ((allocated: number, budget: number) => void) | null = null;

  constructor(maxVRAMMB: number = 16384) {
    this.budget = maxVRAMMB;
    console.log(`[GPU VRAM Allocator] Pipeline initialized. Budget: ${this.budget}MB`);
  }

  attachRenderer(renderer: THREE.WebGPURenderer): void {
    this._renderer = renderer;
    console.log(`[GPU VRAM Allocator] Renderer attached: WebGPU`);
    try {
      const device = (renderer.backend as any)?.device;
      if (device?.limits) {
        console.log(`[GPU VRAM Allocator] Max storage buffer size: ${(device.limits.maxStorageBufferSize / 1024 / 1024).toFixed(1)}MB`);
        console.log(`[GPU VRAM Allocator] Max compute workgroups per dimension: ${device.limits.maxComputeWorkgroupsPerDimension}`);
      }
    } catch { }
  }

  registerParticleCompute(id: string, name: string, count: number): ComputeEffect {
    const vramMB = count * 4 * 4 / (1024 * 1024) + 2;
    const roundedVRAM = Math.round(vramMB * 100) / 100;
    const workgroups = Math.max(1, Math.ceil(count / 64));

    const effect: ComputeEffect = {
      id, name, vramMB: roundedVRAM, workgroups, enabled: true, scale: count,
      dispatch: () => {
        if (!effect.enabled) return;
        if (this.totalVRAM > this.budget) {
          this.onBudgetWarning?.(this.totalVRAM, this.budget);
          return;
        }
      },
      dispose: () => {
        this.totalVRAM = Math.max(0, this.totalVRAM - roundedVRAM);
        this.effects.delete(id);
        console.log(`[GPU VRAM Allocator] Disposed particle compute "${name}". Freed ${roundedVRAM}MB. Total: ${this.totalVRAM.toFixed(1)}MB`);
      },
    };

    this.effects.set(id, effect);
    this.totalVRAM += roundedVRAM;
    console.log(`[GPU VRAM Allocator] Registering particle compute "${name}": ${count.toLocaleString()} particles, ${roundedVRAM}MB VRAM, ${workgroups} workgroups`);
    this.checkBudget();
    return effect;
  }

  registerFluidCompute(id: string, name: string, resolution: number): ComputeEffect {
    const cellCount = resolution * resolution;
    const vramMB = cellCount * 4 * 4 * 4 / (1024 * 1024) + 4;
    const roundedVRAM = Math.round(vramMB * 100) / 100;
    const workgroups = Math.max(1, Math.ceil(resolution / 8));

    const effect: ComputeEffect = {
      id, name, vramMB: roundedVRAM, workgroups, enabled: true, scale: resolution,
      dispatch: () => {
        if (!effect.enabled) return;
        if (this.totalVRAM > this.budget) {
          this.onBudgetWarning?.(this.totalVRAM, this.budget);
          return;
        }
      },
      dispose: () => {
        this.totalVRAM = Math.max(0, this.totalVRAM - roundedVRAM);
        this.effects.delete(id);
        console.log(`[GPU VRAM Allocator] Disposed fluid compute "${name}". Freed ${roundedVRAM}MB. Total: ${this.totalVRAM.toFixed(1)}MB`);
      },
    };

    this.effects.set(id, effect);
    this.totalVRAM += roundedVRAM;
    console.log(`[GPU VRAM Allocator] Registering fluid compute "${name}": ${resolution}x${resolution} grid, ${roundedVRAM}MB VRAM, ${workgroups} workgroups`);
    this.checkBudget();
    return effect;
  }

  registerCustomCompute(id: string, name: string, estimatedVRAMMB: number, dispatchFn: () => void): ComputeEffect {
    const roundedVRAM = Math.round(estimatedVRAMMB * 100) / 100;
    const effect: ComputeEffect = {
      id, name, vramMB: roundedVRAM, workgroups: 1, enabled: true, scale: 0,
      dispatch: () => {
        if (!effect.enabled) return;
        if (this.totalVRAM > this.budget) {
          this.onBudgetWarning?.(this.totalVRAM, this.budget);
          return;
        }
        dispatchFn();
      },
      dispose: () => {
        this.totalVRAM = Math.max(0, this.totalVRAM - roundedVRAM);
        this.effects.delete(id);
        console.log(`[GPU VRAM Allocator] Disposed custom compute "${name}". Freed ${roundedVRAM}MB. Total: ${this.totalVRAM.toFixed(1)}MB`);
      },
    };
    this.effects.set(id, effect);
    this.totalVRAM += roundedVRAM;
    console.log(`[GPU VRAM Allocator] Registering custom compute "${name}": ${roundedVRAM}MB VRAM`);
    this.checkBudget();
    return effect;
  }

  getVRAMSnapshot(): VRAMSnapshot {
    const effects = Array.from(this.effects.values()).map(e => ({
      id: e.id,
      name: e.name,
      vramMB: e.vramMB,
      enabled: e.enabled,
      scale: e.scale,
    }));
    return {
      totalAllocatedMB: Math.round(this.totalVRAM * 100) / 100,
      totalBudgetMB: this.budget,
      effects,
      utilizationPercent: Math.min(100, Math.round((this.totalVRAM / this.budget) * 100)),
    };
  }

  enableEffect(id: string): boolean {
    const effect = this.effects.get(id);
    if (!effect) return false;
    effect.enabled = true;
    console.log(`[GPU VRAM Allocator] Enabled compute "${effect.name}"`);
    return true;
  }

  disableEffect(id: string): boolean {
    const effect = this.effects.get(id);
    if (!effect) return false;
    effect.enabled = false;
    console.log(`[GPU VRAM Allocator] Disabled compute "${effect.name}"`);
    return true;
  }

  getEffect(id: string): ComputeEffect | undefined {
    return this.effects.get(id);
  }

  getAllEffects(): ComputeEffect[] {
    return Array.from(this.effects.values());
  }

  setBudgetMB(mb: number): void {
    const old = this.budget;
    this.budget = mb;
    console.log(`[GPU VRAM Allocator] Budget changed: ${old}MB -> ${mb}MB`);
    this.checkBudget();
  }

  get totalAllocatedMB(): number { return Math.round(this.totalVRAM * 100) / 100; }
  get budgetMB(): number { return this.budget; }

  disposeAll(): void {
    for (const effect of this.effects.values()) effect.dispose();
    this.effects.clear();
    this.totalVRAM = 0;
    this._renderer = null;
    console.log(`[GPU VRAM Allocator] All compute effects disposed.`);
  }

  private checkBudget(): void {
    if (this.totalVRAM > this.budget) {
      const over = (this.totalVRAM - this.budget).toFixed(1);
      console.log(`[GPU VRAM Allocator] WARNING: VRAM budget exceeded! ${this.totalVRAM.toFixed(1)}MB / ${this.budget}MB (${over}MB over)`);
      this.onBudgetWarning?.(this.totalVRAM, this.budget);
    } else {
      const pct = ((this.totalVRAM / this.budget) * 100).toFixed(1);
      console.log(`[GPU VRAM Allocator] VRAM OK: ${this.totalVRAM.toFixed(1)}MB / ${this.budget}MB (${pct}%)`);
    }
  }
}